import { ApplicationInsights, IExtendedConfiguration } from "@microsoft/1ds-analytics-web-js";
import { CorrelationVectorPlugin } from "@microsoft/1ds-cv-js";
import { IUCISModel } from "./Interfaces/interface";

// CommonSchema UTC flag that tells the Aria collector to scrub ext.ingest.clientIp.
const SCRUB_IP_FLAG = 0x100000; // 1048576 decimal

// Query parameters that carry authentication credentials or tokens.
// Matched case-insensitively against every URL the SDK auto-captures.
const SENSITIVE_PARAMS = /^(epct|code|token|access_token|id_token|refresh_token|client_secret)$/i;

/**
 * Strips credential-bearing query parameters and fragment parameters from a URL.
 * Returns the sanitized URL string, or an empty string if the input is unparseable.
 */
export function sanitizeUrl(url: string): string {
    if (!url) {
        return url;
    }
    try {
        const parsed = new URL(url, window.location.href);

        // Scrub sensitive search params
        const searchKeysToRemove: string[] = [];
        for (const key of Array.from(parsed.searchParams.keys())) {
            if (SENSITIVE_PARAMS.test(key)) {
                searchKeysToRemove.push(key);
            }
        }
        for (const key of searchKeysToRemove) {
            parsed.searchParams.delete(key);
        }

        // Scrub sensitive fragment params (e.g. #access_token=...&id_token=...)
        if (parsed.hash.length > 1) {
            const fragmentParams = new URLSearchParams(parsed.hash.substring(1));
            const fragKeysToRemove: string[] = [];
            for (const key of Array.from(fragmentParams.keys())) {
                if (SENSITIVE_PARAMS.test(key)) {
                    fragKeysToRemove.push(key);
                }
            }
            if (fragKeysToRemove.length > 0) {
                for (const key of fragKeysToRemove) {
                    fragmentParams.delete(key);
                }
                const remaining = fragmentParams.toString();
                parsed.hash = remaining ? "#" + remaining : "";
            }
        }

        return parsed.toString();
    } catch {
        return "";
    }
}

/**
 * Sanitizes all URL-bearing fields on a telemetry item to prevent credential leaks.
 * Covers: baseData.refUri, data.refUri, baseData.uri, baseData.targetUri (ICM 783875880).
 */
function sanitizeTelemetryUrls(item: any): void {
    if (item.baseData) {
        if (item.baseData.refUri) {
            item.baseData.refUri = sanitizeUrl(item.baseData.refUri);
        }
        if (item.baseData.uri) {
            item.baseData.uri = sanitizeUrl(item.baseData.uri);
        }
        if (item.baseData.targetUri) {
            item.baseData.targetUri = sanitizeUrl(item.baseData.targetUri);
        }
    }
    if (item.data?.refUri) {
        item.data.refUri = sanitizeUrl(item.data.refUri);
    }
}

// Pipeline plugin: stamps the ScrubIp flag on every event and sanitizes URLs to
// strip credential-bearing query params. Pipeline plugins are wired into the chain
// BEFORE any plugin's initialize() runs, so auto-captured events (pageView, onLoad)
// that fire during initialize() are also covered (ICM 759298390, ICM 783875880).
const scrubIpPlugin: any = {
    identifier: "ScrubIpPlugin",
    // tslint:disable-next-line:no-shadowed-variable
    initialize: (cfg: any, core: any, ext: any) => { /* no-op */ },
    isInitialized: () => true,
    priority: 190,
    processTelemetry: (item: any, itemCtx: any) => {
        item.ext = item.ext || {};
        item.ext.utc = item.ext.utc || {};
        // tslint:disable-next-line:no-bitwise
        item.ext.utc.eventFlags = (item.ext.utc.eventFlags || 0) | SCRUB_IP_FLAG;

        sanitizeTelemetryUrls(item);

        if (itemCtx) {
            itemCtx.processNext(item);
        }
    }
};


const analytics: ApplicationInsights = new ApplicationInsights();
let cvPlugin: CorrelationVectorPlugin = new CorrelationVectorPlugin();
const ucisData: IUCISModel = window.ucis;
let config: IExtendedConfiguration = {
    channelConfiguration: { // Post channel configuration
        eventsLimitInMem: 50
    },
    extensions: [
        cvPlugin,
        scrubIpPlugin
    ],
    instrumentationKey: ucisData?.OneDsToken,
    propertyConfiguration: { // Properties Plugin configuration
        // populateBrowserInfo: true,
        populateOperatingSystemInfo: true,
        userAgent: window.navigator.userAgent,
    },
    webAnalyticsConfiguration: { // Web Analytics Plugin configuration
        autoCapture: {
            click: true,
            jsError: true,
            onLoad: true,
            onUnload: true,
            pageView: true,
            resize: true,
            scroll: true
        }
    }
};

//Initialize SDK
analytics.initialize(config, []);


export default analytics;
